import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { match as Match } from "react-router-dom";
import { Location } from "history";
import history from "../../../../history";
import { useIntl } from "react-intl";
import {
  navTo,
  EnumPaths,
  getServicesPathByTypology,
} from "../../../../utils/navigation";
import ListServiceCategories from "./categories-list";
import {
  EnumCategoryType,
  EnumServiceType,
  SearchableCategorySortableFields,
  SearchableServiceSortableFields,
  SearchableSortDirection,
} from "../../../../lib/ground-aws-graphql-core/api/graphql/types";
import { Category } from "../../../../lib/ground-aws-graphql-core/models/Category";
import { Service } from "../../../../lib/ground-aws-graphql-core/models/Service";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import contextStore from "../../../../redux/store";
import PageSubheader from "../../../../components/PageSubheader";
import { EnumAction } from "../../../../utils/types";
import { getLocale } from "../../../../lang";
import { TableChangeParams } from "../../../../components/Table/types";
import TableFooter from "../../../../components/Table/Footer";
import { DEFAULT_LIMIT } from "../../../../utils/config";
import { toast } from "react-toastify";

interface Props {
  match: Match<{ cid: string }>;
  serviceType: EnumServiceType;
  categoryType: EnumCategoryType;
  location: Location<{
    pageIndex: number;
    limit: number;
    type: string;
  }>;
}

const ListServices = ({
  serviceType,
  categoryType,
  match,
  location,
}: Props): JSX.Element => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [initialParams, setInitialParams] = useState<TableChangeParams | null>(
    location?.state
  );

  const locale = contextStore.useStoreState(s => s.settings.locale);

  const searchAllServices = GroundGraphqlContextStore.useStoreActions(
    a => a.service.searchAllServices
  );

  const categories = GroundGraphqlContextStore.useStoreState(
    s => s.category.categories.items
  );
  const total = GroundGraphqlContextStore.useStoreState(
    s => s.category.categories.total
  );

  const services = GroundGraphqlContextStore.useStoreState(s =>
    serviceType === EnumServiceType.SPACE
      ? s.service.spaces.items
      : s.service.services.items
  );

  const setService = GroundGraphqlContextStore.useStoreActions(
    a => a.service.setService
  );

  const searchCategories = GroundGraphqlContextStore.useStoreActions(
    a => a.category.searchCategories
  );

  const updateCategoryOrder = GroundGraphqlContextStore.useStoreActions(
    a => a.category.updateCategoryOrder
  );

  const updateServiceOrder = GroundGraphqlContextStore.useStoreActions(
    a => a.service.updateServiceOrder
  );

  const modalCategoryAction = contextStore.useStoreState(
    s => s.category.modalCategoryAction
  );
  const modalCategorySelected = contextStore.useStoreState(
    s => s.category.modalCategorySelected
  );
  const modalCreateUpdateCategoryIsOpen = contextStore.useStoreState(
    s => s.category.modalCreateUpdateCategoryIsOpen
  );
  const modalDuplicateCategoryIsOpen = contextStore.useStoreState(
    s => s.category.modalDuplicateCategoryIsOpen
  );

  const setModalCategoryAction = contextStore.useStoreActions(
    a => a.category.setModalCategoryAction
  );
  const setModalCategorySelected = contextStore.useStoreActions(
    a => a.category.setModalCategorySelected
  );
  const setModalCreateUpdateCategoryIsOpen = contextStore.useStoreActions(
    a => a.category.setModalCreateUpdateCategoryIsOpen
  );
  const setModalDuplicateCategoryIsOpen = contextStore.useStoreActions(
    a => a.category.setModalDuplicateCategoryIsOpen
  );

  const fetchCategories = useCallback(
    (pageIndex = 0, limit = DEFAULT_LIMIT) => {
      return searchCategories({
        filter: {
          type: { eq: categoryType },
          categoryCenterId: { eq: match.params.cid },
        },
        sort: {
          field: SearchableCategorySortableFields.position,
          direction: SearchableSortDirection.asc,
        },
        from: pageIndex * limit,
        limit,
        locale: getLocale(locale).backend_locale,
      });
    },
    [categoryType, match.params.cid, locale, searchCategories]
  );

  const fetchAllServices = useCallback(() => {
    return searchAllServices({
      limit: DEFAULT_LIMIT,
      filter: {
        centerServicesId: { eq: match.params.cid },
        type: { eq: serviceType },
      },
      sort: {
        field: SearchableServiceSortableFields.position,
        direction: SearchableSortDirection.asc,
      },
    });
  }, [serviceType, match.params.cid, locale, searchAllServices]);

  const fetchData = useCallback(
    async (pageIndex = 0, loader = true, limit?: number) => {
      if (loader) setLoading(true);
      try {
        await Promise.all([
          fetchCategories(pageIndex, limit),
          fetchAllServices(),
        ]);
      } finally {
        if (loader) setLoading(false);
      }
    },
    [fetchCategories]
  );

  useEffect(() => {
    fetchData();
  }, [serviceType, categoryType, match.params.cid, fetchData]);

  const handleAddNewService = (category: Category) => {
    setService(null);
    const centerId = match.params.cid;

    const path = getServicesPathByTypology(serviceType);

    const navPath = navTo(`${path}/${EnumPaths.CREATION_MODE}`, centerId);

    history.push(navPath, { category });
  };

  const handleOrderCategory = useCallback(
    async (
      id: string,
      buildingId: string,
      beforeId?: string,
      afterId?: string
    ) => {
      try {
        await updateCategoryOrder({
          id,
          buildingId,
          beforeId,
          afterId,
          ...(initialParams?.pageIndex &&
            initialParams?.limit && {
              from: initialParams?.pageIndex * initialParams?.limit,
            }),
          limit: initialParams?.limit,
        });

        toast(
          intl.formatMessage({
            id: `page.list.${serviceType.toLowerCase()}s.category.moved.success`,
          }),
          {
            type: "success",
          }
        );
      } catch (err) {
        toast(
          intl.formatMessage({
            id: `page.list.${serviceType.toLowerCase()}s.category.moved.error`,
          }),
          {
            type: "error",
          }
        );
      }
    },
    [updateCategoryOrder, initialParams]
  );

  const handleOrderService = useCallback(
    async (
      id: string,
      categoryId: string,
      buildingId: string,
      beforeId?: string,
      afterId?: string
    ) => {
      try {
        await updateServiceOrder({
          id,
          categoryId,
          buildingId,
          beforeId,
          afterId,
        });

        toast(
          intl.formatMessage({
            id: `page.list.${serviceType.toLowerCase()}s.service.moved.success`,
          }),
          {
            type: "success",
          }
        );
      } catch (err) {
        toast(
          intl.formatMessage({
            id: `page.list.${serviceType.toLowerCase()}s.service.moved.error`,
          }),
          {
            type: "error",
          }
        );
      }
    },
    [updateServiceOrder]
  );

  const handleEditCategory = (category: Category) => {
    setModalCategoryAction(EnumAction.UPDATE);
    setModalCategorySelected(category);
    setModalCreateUpdateCategoryIsOpen(true);
  };

  const handleDuplicateCategory = (category: Category) => {
    setModalCategoryAction(EnumAction.DUPLICATE);
    setModalCategorySelected(category);
    setModalDuplicateCategoryIsOpen(true);
  };

  const handleClickService = (_e: any, service: Service) => {
    const path = getServicesPathByTypology(serviceType);
    history.push(navTo(`${path}/${service.id}`, match.params.cid));
  };

  const handleEditService = (_e: any, service: Service) => {
    const path = getServicesPathByTypology(serviceType);
    history.push(
      navTo(`${path}/${service.id}/${EnumPaths.EDIT_MODE}`, match.params.cid)
    );
  };

  const handleAddCategory = () => {
    setModalCategoryAction(EnumAction.CREATE);
    setModalCategorySelected(null);
    setModalCreateUpdateCategoryIsOpen(true);
  };

  const handleOnChange = (changeParams: TableChangeParams) => {
    setInitialParams(changeParams);
    fetchData(changeParams.pageIndex, true, changeParams.limit);
  };

  return loading ? (
    <div className="loading" />
  ) : (
    <>
      <Helmet>
        <title>
          {intl.formatMessage({
            id: `page.list.${serviceType.toLowerCase()}s.document.title`,
          })}
        </title>
      </Helmet>

      <PageSubheader
        title={`page.list.${serviceType}.title`}
        buttonRightTitle="general.add.CATEGORY"
        buttonRightAction={handleAddCategory}
        buttonRightId="btn-add-category"
      />

      <div className="px-8 pt-8">
        <ListServiceCategories
          loading={loading}
          serviceType={serviceType}
          handleEditService={handleEditService}
          handleClickService={handleClickService}
          fetchData={fetchData}
          match={match}
          categories={categories}
          services={services}
          modalCreateUpdateCategoryIsOpen={modalCreateUpdateCategoryIsOpen}
          setModalCreateUpdateCategoryIsOpen={
            setModalCreateUpdateCategoryIsOpen
          }
          modalDuplicateCategoryIsOpen={modalDuplicateCategoryIsOpen}
          setModalDuplicateCategoryIsOpen={setModalDuplicateCategoryIsOpen}
          modalCategoryAction={modalCategoryAction}
          modalCategorySelected={modalCategorySelected}
          handleEditCategory={handleEditCategory}
          handleDuplicateCategory={handleDuplicateCategory}
          handleAddService={handleAddNewService}
          handleOrderCategory={handleOrderCategory}
          handleOrderService={handleOrderService}
        />

        <TableFooter
          initialParams={initialParams}
          onChange={handleOnChange}
          paginationLimit={DEFAULT_LIMIT}
          paginationTotal={total}
          limitChoiceEnabled
        />
      </div>
    </>
  );
};

export default ListServices;
